import { useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/utils'
import { Package, TrendingUp, ChevronLeft, ChevronRight, Images, X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { ClothingItem, ClothingVariant } from '@/shared/types'

interface VariantDrawerProps {
  item: ClothingItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function VariantDrawer({ item, open, onOpenChange }: VariantDrawerProps) {
  const [galleryState, setGalleryState] = useState<{
    images: string[]
    currentIndex: number
  } | null>(null)

  if (!item) return null

  const totalStock = item.variants.reduce((sum, v) => sum + v.stockQty, 0)
  const availableVariants = item.variants.filter((v) => v.stockQty > 0).length
  const minPrice = Math.min(...item.variants.map((v) => v.price))
  const maxPrice = Math.max(...item.variants.map((v) => v.price))

  const handleImageClick = (images: string[], startIndex: number) => {
    setGalleryState({ images, currentIndex: startIndex })
  }

  const closeGallery = () => {
    setGalleryState(null)
  }

  const navigateGallery = (index: number) => {
    if (galleryState) {
      setGalleryState({ ...galleryState, currentIndex: index })
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0">
          {/* Header with gradient background */}
          <div className="sticky top-0 z-10 bg-gradient-to-b from-muted/80 to-background pb-4 pt-6 px-6">
            <SheetHeader className="mb-0">
              <div className="flex items-start gap-4 pr-8">
                <div className="relative w-20 h-24 sm:w-24 sm:h-28 rounded-xl overflow-hidden flex-shrink-0 border-2 border-background shadow-lg bg-muted">
                  <img
                    src={item.imageUrl || '/placeholder-image.png'}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0 py-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {item.brand}
                  </p>
                  <SheetTitle className="text-xl leading-tight mb-2">{item.name}</SheetTitle>
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </SheetHeader>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div className="bg-background rounded-lg p-3 border">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Package className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Total Stock</span>
                </div>
                <p className="text-lg font-bold text-foreground">{totalStock}</p>
              </div>
              <div className="bg-background rounded-lg p-3 border">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Available</span>
                </div>
                <p className="text-lg font-bold text-foreground">
                  {availableVariants}/{item.variants.length}
                </p>
              </div>
              <div className="bg-background rounded-lg p-3 border">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="text-xs font-medium">Price Range</span>
                </div>
                <p className="text-lg font-bold text-foreground">
                  {minPrice === maxPrice ? `₹${minPrice}` : `₹${minPrice}-${maxPrice}`}
                </p>
              </div>
            </div>
          </div>

          {/* Variants List */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-foreground">
                All Variants
              </h4>
              <Badge variant="secondary" className="font-normal">
                {item.variants.length} total
              </Badge>
            </div>

            <div className="space-y-3">
              {item.variants.map((variant, index) => (
                <VariantCard 
                  key={variant.id} 
                  variant={variant} 
                  index={index}
                  onImageClick={handleImageClick}
                />
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Image Gallery Lightbox */}
      {galleryState && (
        <ImageGallery
          images={galleryState.images}
          currentIndex={galleryState.currentIndex}
          onClose={closeGallery}
          onNavigate={navigateGallery}
        />
      )}
    </>
  )
}

interface VariantCardProps {
  variant: ClothingVariant
  index: number
  onImageClick?: (images: string[], startIndex: number) => void
}

function VariantCard({ variant, index, onImageClick }: VariantCardProps) {
  const isOutOfStock = variant.stockQty === 0
  const primaryImageSrc = typeof variant.imageUrl === 'string' ? variant.imageUrl : ''
  
  // Combine primary and additional images
  const allImages = [
    primaryImageSrc,
    ...(variant.imageUrls || []),
  ].filter(Boolean)
  
  const hasMultipleImages = allImages.length > 1

  return (
    <div
      className={cn(
        'group relative flex items-center gap-4 p-4 rounded-xl border transition-all duration-200',
        'hover:shadow-md hover:border-primary/20',
        isOutOfStock 
          ? 'bg-muted/30 border-dashed' 
          : 'bg-card hover:bg-accent/5'
      )}
      style={{
        animationDelay: `${index * 50}ms`,
      }}
    >
      {/* Variant Image with Gallery Indicator */}
      {primaryImageSrc ? (
        <div 
          className={cn(
            "relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border bg-muted",
            hasMultipleImages && "cursor-pointer"
          )}
          onClick={() => hasMultipleImages && onImageClick?.(allImages, 0)}
        >
          <img
            src={primaryImageSrc}
            alt={`${variant.size} / ${variant.color}`}
            className={cn(
              'w-full h-full object-cover transition-transform',
              isOutOfStock && 'opacity-50 grayscale',
              hasMultipleImages && 'group-hover:scale-105'
            )}
          />
          {/* Gallery indicator badge */}
          {hasMultipleImages && (
            <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
              <Images className="w-2.5 h-2.5" />
              <span>{allImages.length}</span>
            </div>
          )}
        </div>
      ) : (
        <div className={cn(
          'w-16 h-16 rounded-lg flex-shrink-0 border flex items-center justify-center',
          isOutOfStock ? 'bg-muted/50' : 'bg-muted'
        )}>
          <span className="text-xs font-medium text-muted-foreground">
            {variant.size}
          </span>
        </div>
      )}

      {/* Variant Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={cn(
            'font-medium',
            isOutOfStock ? 'text-muted-foreground' : 'text-foreground'
          )}>
            {variant.size}
          </span>
          <span className="text-muted-foreground">/</span>
          <div className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-full border shadow-sm"
              style={{
                backgroundColor: getColorValue(variant.color),
              }}
            />
            <span className={cn(
              'text-sm',
              isOutOfStock ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {variant.color}
            </span>
          </div>
        </div>
        {variant.sku && (
          <p className="text-xs text-muted-foreground font-mono">
            SKU: {variant.sku}
          </p>
        )}
        {/* Thumbnail strip for additional images */}
        {hasMultipleImages && (
          <div className="flex gap-1 mt-2">
            {allImages.slice(0, 4).map((img, imgIdx) => (
              <button
                key={imgIdx}
                className={cn(
                  "w-6 h-6 rounded border overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all",
                  imgIdx === 0 && "ring-1 ring-primary"
                )}
                onClick={() => onImageClick?.(allImages, imgIdx)}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            {allImages.length > 4 && (
              <div className="w-6 h-6 rounded border bg-muted flex items-center justify-center text-[10px] text-muted-foreground">
                +{allImages.length - 4}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Price and Stock */}
      <div className="text-right flex-shrink-0">
        <p className={cn(
          'text-base font-bold mb-0.5',
          isOutOfStock ? 'text-muted-foreground' : 'text-foreground'
        )}>
          ₹{variant.price}
        </p>
        <div
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            isOutOfStock
              ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
              : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
          )}
        >
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              isOutOfStock ? 'bg-orange-500' : 'bg-emerald-500'
            )}
          />
          {isOutOfStock ? 'Out of stock' : `${variant.stockQty} in stock`}
        </div>
      </div>
    </div>
  )
}

// Image Gallery Lightbox Component
interface ImageGalleryProps {
  images: string[]
  currentIndex: number
  onClose: () => void
  onNavigate: (index: number) => void
}

function ImageGallery({ images, currentIndex, onClose, onNavigate }: ImageGalleryProps) {
  const goNext = () => {
    onNavigate((currentIndex + 1) % images.length)
  }
  
  const goPrev = () => {
    onNavigate((currentIndex - 1 + images.length) % images.length)
  }

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close Button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
        onClick={onClose}
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Navigation - Previous */}
      {images.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 text-white hover:bg-white/20 h-12 w-12"
          onClick={(e) => {
            e.stopPropagation()
            goPrev()
          }}
        >
          <ChevronLeft className="w-8 h-8" />
        </Button>
      )}

      {/* Main Image */}
      <div 
        className="max-w-[90vw] max-h-[85vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
        
        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Navigation - Next */}
      {images.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 text-white hover:bg-white/20 h-12 w-12"
          onClick={(e) => {
            e.stopPropagation()
            goNext()
          }}
        >
          <ChevronRight className="w-8 h-8" />
        </Button>
      )}

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 bg-black/40 p-2 rounded-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, idx) => (
            <button
              key={idx}
              className={cn(
                "w-12 h-12 rounded-md overflow-hidden border-2 transition-all",
                idx === currentIndex 
                  ? "border-white opacity-100" 
                  : "border-transparent opacity-60 hover:opacity-100"
              )}
              onClick={() => onNavigate(idx)}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Helper function to convert color names to CSS values
function getColorValue(colorName: string): string {
  const colorMap: Record<string, string> = {
    // Common colors
    white: '#ffffff',
    black: '#000000',
    red: '#ef4444',
    blue: '#3b82f6',
    green: '#22c55e',
    yellow: '#eab308',
    orange: '#f97316',
    purple: '#a855f7',
    pink: '#ec4899',
    brown: '#92400e',
    gray: '#6b7280',
    grey: '#6b7280',
    navy: '#1e3a5f',
    beige: '#d4c4a8',
    cream: '#fffdd0',
    maroon: '#800000',
    olive: '#808000',
    teal: '#008080',
    coral: '#ff7f50',
    gold: '#ffd700',
    silver: '#c0c0c0',
    tan: '#d2b48c',
    khaki: '#f0e68c',
    burgundy: '#800020',
    charcoal: '#36454f',
    // Default fallback
  }

  const lowerColor = colorName.toLowerCase().trim()
  return colorMap[lowerColor] || '#9ca3af' // Default to gray if not found
}
